import { MouseEvent } from 'react';
import clsx from 'clsx';
import { Bookmark, Check, Clock, MapPin, Send, SkipForward, TriangleAlert } from 'lucide-react';
import type { JhApplication } from '@/services/job-hunter.service';
import { REMOTE_LABEL, scoreTone, timeAgo } from '@/lib/job-hunter/format';
import { useJobHunter } from '@/lib/job-hunter/store';
import { AgentChip, AtsBadge, CountryTag, Pill, ScoreRing, SignalBadge, StatusBadge, VisaLine } from './ui';

export function MatchCard({ app, onOpen, onDecide, busy, compact }: {
  app: JhApplication;
  onOpen: () => void;
  onDecide?: (action: 'apply' | 'save' | 'skip') => void;
  busy?: string | null;
  compact?: boolean;
}) {
  const j = app.job;
  const e = app.evaluation;
  const stop = (fn: () => void) => (ev: MouseEvent) => { ev.stopPropagation(); fn(); };
  const open = ['proposed', 'saved'].includes(app.status) && !app.jobClosed;
  const { agents } = useJobHunter();
  const agent = app.agentUid ? agents?.find((a) => a.agentUid === app.agentUid) : undefined;
  return (
    <article className={clsx('jh-match', app.jobClosed && 'is-closed')} style={{ ['--jh-accent' as string]: scoreTone(e.fitScore).color }} onClick={onOpen}
      role="button" tabIndex={0} onKeyDown={(ev) => { if (ev.key === 'Enter') onOpen(); }}>
      <ScoreRing score={e.fitScore} size={compact ? 46 : 56} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="jh-match-title truncate">{j?.title ?? 'Job'}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-text-secondary">
              <span className="font-semibold text-text-primary">{j?.employerName}</span>
              <CountryTag cc={e.country ?? j?.country} city={j?.city} compact={compact} />
              {!compact && <span className="inline-flex items-center gap-1"><MapPin size={12} />{REMOTE_LABEL[e.remoteType ?? j?.remoteType ?? 'unknown']}</span>}
              <span className="inline-flex items-center gap-1 text-text-muted"><Clock size={12} />{timeAgo(j?.postedAt ?? j?.firstSeenAt)}</span>
            </div>
          </div>
          {onDecide && open && (
            <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
              <button type="button" title="Approve & prepare" disabled={!!busy} onClick={stop(() => onDecide('apply'))}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-on-primary shadow-soft transition hover:brightness-110 disabled:opacity-50">
                <Send size={13} />{busy === 'apply' ? 'Preparing…' : 'Apply'}
              </button>
              {app.status !== 'saved' && <button type="button" title="Save for later" disabled={!!busy} onClick={stop(() => onDecide('save'))} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-secondary hover:border-info hover:text-info disabled:opacity-50"><Bookmark size={14} /></button>}
              <button type="button" title="Skip" disabled={!!busy} onClick={stop(() => onDecide('skip'))} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-secondary hover:border-danger hover:text-danger disabled:opacity-50"><SkipForward size={14} /></button>
            </div>
          )}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <SignalBadge signal={e.sponsorshipSignal} />
          {j && <AtsBadge fromAts={j.fromAts} type={j.fromAts ? undefined : j.sourceType} />}
          {app.status !== 'proposed' && <StatusBadge status={app.status} />}
          {app.jobClosed && <Pill tone="danger">Closed</Pill>}
          {e.isAgency && <Pill tone="warning">Agency</Pill>}
          {app.duplicateWarning && <Pill tone="warning">Applied before</Pill>}
          {agent && (agents?.length ?? 0) > 1 && <AgentChip name={agent.name} color={agent.color} />}
          <span className="jh-chip">#{app.ref}</span>
        </div>
        {!compact && <div className="mt-2"><VisaLine route={e.visaRoute} register={e.registerMatch} /></div>}
        {!compact && e.sponsorshipQuote && e.sponsorshipSignal === 'explicit' && <p className="jh-quote mt-2.5 line-clamp-2">“{e.sponsorshipQuote}”</p>}
        {!compact && (e.fitReasons.length > 0 || e.redFlags.length > 0) && (
          <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-text-secondary">
            {e.fitReasons.slice(0, 2).map((r) => <span key={r} className="inline-flex items-center gap-1"><Check size={12} className="text-success" />{r}</span>)}
            {e.redFlags.slice(0, 1).map((r) => <span key={r} className="inline-flex items-center gap-1"><TriangleAlert size={12} className="text-warning" />{r}</span>)}
          </div>
        )}
      </div>
    </article>
  );
}
